import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// helpers
import { delayHttpRequest } from '../helpers/globalHelper'

// services
import { communicateService } from '../services/communicationService'

// types
import {
  Message,
  InitiatorType,
  MessageType,
  EmployeeMessage,
  CreateEmployeeMessagePayload,
  PatchEmployeeMessagePayload
} from '../types/communicateTypes'

type Store = {
  messages: Message[]
  employeeMessages: EmployeeMessage[] | null
  loading: boolean
  error: string | null
  clearData: () => void
  clearConversation: () => void
  fetchAllMessages: (id: number) => void
  replyToMessage: (
    id: number,
    messageKey: number,
    data: { message: string }
  ) => void
  createNewMessage: (
    id: number,
    data: { message: string; subject: string }
  ) => void
  messagesCount: (id) => void
  employees: any
  getEmployeesByDept: (
    entityId: number,
    storeId: number,
    deptId: number
  ) => void
  fetchEmployeeMessages: (onboardingKey: number) => Promise<void>
  createEmployeeMessage: (
    onboardingKey: number,
    data: CreateEmployeeMessagePayload
  ) => Promise<void>
  patchEmployeeMessage: (
    onboardingKey: number,
    essEmployeeMessageKey: number,
    data: PatchEmployeeMessagePayload
  ) => Promise<void>
  chattedEmployees: any[] | null
  getChattedEmployees: (obKey: number) => Promise<void>
  getConversation: (
    obkey: number,
    pUserKey?: number,
    pObKey?: number
  ) => Promise<void>
  updateIsEmployeeMessagingEnabled: (
    id: number,
    isEnabled: boolean
  ) => Promise<void>
  conversation: any | null
  unreadMessage: any
}

const useCommunicateStore = create(
  devtools<Store>((set) => ({
    messages: null,
    employeeMessages: null,
    loading: false,
    error: null,
    employees: null,
    chattedEmployees: null,
    conversation: null,
    clearData: () =>
      set({
        messages: null,
        employeeMessages: null,
        loading: false,
        error: null,
        chattedEmployees: null
      }),
    clearConversation: () =>
      set((state) => ({
        ...state,
        conversation: null
      })),
    fetchAllMessages: async (id) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const _messages = await communicateService.fetchAllMessages(id)

        set((state) => ({ ...state, messages: _messages }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    replyToMessage: async (id, messageKey, data) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const _message = await communicateService.replyToMessage(
          id,
          messageKey,
          data
        )
        const _messages = await communicateService.fetchAllMessages(id)

        set((state) => ({ ...state, messages: _messages }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    createNewMessage: async (id, data) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const _messageId = await communicateService.createNewMessage(id, data)
        // const _messages = await communicateService.fetchAllMessages(id)

        // set((state) => ({ ...state, messages: _messages }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    messagesCount: async (id) => {
      set((state) => ({ ...state, loading: true, error: null }))
      try {
        await delayHttpRequest()
        const _messages = await communicateService.messagesCount(id)

        set((state) => ({ ...state, unreadMessage: _messages }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    getEmployeesByDept: async (entityId, storeId, deptId) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const _employees: any = await communicateService.getEmployeesByDept(
          entityId,
          storeId,
          deptId
        )

        set((state) => ({
          ...state,
          employees: _employees?.data?.employees || []
        }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    fetchEmployeeMessages: async (onboardingKey) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const response =
          await communicateService.fetchEmployeeMessages(onboardingKey)

        set((state) => ({ ...state, employeeMessages: response?.data || [] }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    createEmployeeMessage: async (onboardingKey, data) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        await communicateService.createEmployeeMessage(onboardingKey, data)
        const response =
          await communicateService.fetchEmployeeMessages(onboardingKey)

        set((state) => ({ ...state, employeeMessages: response?.data || [] }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    patchEmployeeMessage: async (
      onboardingKey,
      essEmployeeMessageKey,
      data
    ) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        await communicateService.patchEmployeeMessage(
          onboardingKey,
          essEmployeeMessageKey,
          data
        )
        const response =
          await communicateService.fetchEmployeeMessages(onboardingKey)

        set((state) => ({ ...state, employeeMessages: response?.data || [] }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    getChattedEmployees: async (obkey: number) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const response = await communicateService.getChattedEmployees(obkey)
        set((state) => ({ ...state, chattedEmployees: response?.data || [] }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    getConversation: async (
      obkey: number,
      pUserKey?: number,
      pObKey?: number
    ) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        const response = await communicateService.getConversation(
          obkey,
          pUserKey,
          pObKey
        )
        set((state) => ({ ...state, conversation: response }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    },
    updateIsEmployeeMessagingEnabled: async (
      id: number,
      isEnabled: boolean
    ) => {
      set((state) => ({ ...state, loading: true }))
      try {
        await delayHttpRequest()
        await communicateService.updateIsEmployeeMessagingEnabled(id, isEnabled)
        const response = await communicateService.getConversation(id)
        set((state) => ({ ...state, conversation: response }))
      } catch (err) {
        set((state) => ({ ...state, error: err.message }))
      } finally {
        set((state) => ({ ...state, loading: false }))
      }
    }
  }))
)

export { useCommunicateStore }
